/**
 * AllBaise i18n Service — pre-imported locales for all 33 languages.
 *
 * All locale JSON files are imported at build time (CRA requirement).
 * Switching language just swaps the reference — no dynamic imports.
 *
 * Usage:
 *   import i18n from "@/services/i18nService";
 *   await i18n.init();
 *   i18n.t("Settings");        // "Paramètres" in French
 *   i18n.t("common.save");     // "Enregistrer"
 *   i18n.currentLang           // { code, name, flagEmoji, direction }
 *
 * Add a new language:
 *   import en from "@/locales/en.json";
 *   LOCALE_MAP.en = en;
 */
import languagesData from "@/locales/languages.json";

// ── Pre-import ALL locale files at build time ──
// This is the only way CRA/webpack can resolve them.
import en from "@/locales/en.json";
import af from "@/locales/af.json";
import am from "@/locales/am.json";
import ar from "@/locales/ar.json";
import de from "@/locales/de.json";
import es from "@/locales/es.json";
import fa from "@/locales/fa.json";
import fr from "@/locales/fr.json";
import ha from "@/locales/ha.json";
import he from "@/locales/he.json";
import hi from "@/locales/hi.json";
import ig from "@/locales/ig.json";
import it from "@/locales/it.json";
import mg from "@/locales/mg.json";
import nl from "@/locales/nl.json";
import nr from "@/locales/nr.json";
import nso from "@/locales/nso.json";
import om from "@/locales/om.json";
import pt from "@/locales/pt.json";
import ru from "@/locales/ru.json";
import sn from "@/locales/sn.json";
import ss from "@/locales/ss.json";
import st from "@/locales/st.json";
import sw from "@/locales/sw.json";
import tn from "@/locales/tn.json";
import ts from "@/locales/ts.json";
import ur from "@/locales/ur.json";
import ve from "@/locales/ve.json";
import xh from "@/locales/xh.json";
import yo from "@/locales/yo.json";
import zh from "@/locales/zh.json";
import zu from "@/locales/zu.json";

const LOCALE_MAP = { en, af, am, ar, de, es, fa, fr, ha, he, hi, ig, it, mg, nl, nr, nso, om, pt, ru, sn, ss, st, sw, tn, ts, ur, ve, xh, yo, zh, zu };

class I18nService {
  constructor() {
    this._translations = {};
    this._currentLang = null;
    this._languages = languagesData || [];
    this._listeners = [];
  }

  async init() {
    const saved = localStorage.getItem("obaise_language") || "en";
    this.setLanguageSync(saved);
    return this;
  }

  getLanguages() {
    return this._languages.filter(l => l.enabled !== false);
  }

  get currentLang() {
    return this._currentLang || this._languages.find(l => l.code === "en") || { code: "en", direction: "ltr" };
  }

  /**
   * Translate text.
   *
   * Supports:
   *   i18n.t("common.save")       — dotted key lookup
   *   i18n.t("Save")              — English text as key
   *   i18n.t("{n} items", {n: 3}) — param substitution
   */
  t(input, params = {}) {
    if (!input) return input;

    // 1. Try dotted key lookup ("common.save")
    const keys = input.split(".");
    let val = this._translations;
    for (const k of keys) {
      val = val?.[k];
    }
    if (typeof val === "string") return this._interpolate(val, params);

    // 2. Try the input as a flat key ("Settings")
    const flat = this._translations?.[input];
    if (typeof flat === "string") return this._interpolate(flat, params);

    // 3. Return input as-is (English fallback)
    return this._interpolate(input, params);
  }

  _interpolate(str, params) {
    if (!params || Object.keys(params).length === 0) return str;
    return str.replace(/\{(\w+)\}/g, (_, p) => params[p] !== undefined ? String(params[p]) : `{${p}}`);
  }

  /**
   * Set language synchronously (no dynamic import needed).
   */
  setLanguageSync(code) {
    const lang = this._languages.find(l => l.code === code);
    if (!lang) return false;

    const translations = LOCALE_MAP[code];
    if (!translations) return false;

    this._translations = translations;
    this._currentLang = lang;
    localStorage.setItem("obaise_language", code);

    document.documentElement.setAttribute("lang", code);
    document.documentElement.setAttribute("dir", lang.direction || "ltr");
    document.documentElement.classList.toggle("rtl", lang.direction === "rtl");

    this._listeners.forEach(fn => fn(lang));
    return true;
  }

  async setLanguage(code) {
    return this.setLanguageSync(code);
  }

  onChange(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter(f => f !== fn);
    };
  }
}

const i18n = new I18nService();
export default i18n;
